// API van AI Voortgang (/Api/voortgang). Verwijderen/terugtrekken gebeurt in de schermen
// zelf (achter bevestig()), zodat het vangnet bij de aanroep staat.
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { leesJson } from '../../utils/apiFout';

const BASIS = `${BACKEND_URL}/Api/voortgang`;

export async function api(pad, { method = 'GET', body, wat = 'AI Voortgang' } = {}) {
  const r = await authFetch(`${BASIS}${pad}`, {
    method,
    headers: body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return leesJson(r, wat);
}

export const lijstProjecten = (archief = false) => api(`/projecten${archief ? '?archief=true' : ''}`, { wat: 'Projecten laden' });
export const haalProject = (id) => api(`/projecten/${id}`, { wat: 'Project laden' });
export const haalSjablonen = () => api('/sjablonen', { wat: 'Sjablonen laden' });
export const haalPortaalAccounts = (projectAirtableId) => api(
  `/portaal-accounts${projectAirtableId ? `?project_airtable_id=${encodeURIComponent(projectAirtableId)}` : ''}`,
  { wat: 'Klantportaal-accounts laden' });
export const nieuwProject = (body) => api('/projecten', { method: 'POST', body, wat: 'Project aanmaken' });
export const wijzigProject = (id, body) => api(`/projecten/${id}`, { method: 'PATCH', body, wat: 'Project opslaan' });
export const nieuweVersie = (optieId, body) => api(`/opties/${optieId}/versies`, { method: 'POST', body, wat: 'Versie toevoegen' });
export const vraagAkkoord = (faseId) => api(`/fases/${faseId}/akkoord-vragen`, { method: 'POST', wat: 'Akkoord vragen' });
export const trekTerug = (faseId) => api(`/fases/${faseId}/terugtrekken`, { method: 'POST', wat: 'Terugtrekken' });
export const zetOvergeslagen = (faseId, overslaan) => api(`/fases/${faseId}/overslaan`, { method: 'POST', body: { overslaan }, wat: overslaan ? 'Fase overslaan' : 'Fase terugzetten' });
export const nieuweOptie = (itemId, label) => api(`/items/${itemId}/opties`, { method: 'POST', body: { label }, wat: 'Optie toevoegen' });
export const hernoemOptie = (optieId, label) => api(`/opties/${optieId}`, { method: 'PATCH', body: { label }, wat: 'Optie hernoemen' });
export const zetJuridisch = (itemId, juridisch) => api(`/items/${itemId}`, { method: 'PATCH', body: { juridisch }, wat: 'Opslaan' });
export const haalBeoordelaars = (projectId) => api(`/projecten/${projectId}/beoordelaars`, { wat: 'Beoordelaars laden' });
export const zetRol = (projectId, portalUserId, rol) => api(`/projecten/${projectId}/beoordelaars/${encodeURIComponent(portalUserId)}`, { method: 'PUT', body: { rol }, wat: 'Rol opslaan' });
export const ROLLEN = {
  approver: { label: 'Geeft akkoord', uitleg: 'tekent voor elk onderdeel' },
  legal: { label: 'Juridisch akkoord', uitleg: 'tekent alleen waar juridisch akkoord nodig is' },
  viewer: { label: 'Kijkt mee', uitleg: 'ziet alles, beslist niet' },
};
// Upload in blokken via de engine (privé blob-opslag). onVoortgang(0..1).
export async function uploadBestand(optieId, bestand, label, onVoortgang) {
  const start = await api(`/opties/${optieId}/upload`, {
    method: 'POST', body: { naam: bestand.name, type: bestand.type, grootte: bestand.size, label }, wat: 'Upload starten' });
  const blok = start.blokgrootte;
  const aantal = Math.max(1, Math.ceil(bestand.size / blok));
  for (let nr = 0; nr < aantal; nr += 1) {
    const stuk = bestand.slice(nr * blok, Math.min(bestand.size, (nr + 1) * blok));
    let poging = 0;
    // Een blok mag opnieuw: staged blocks met hetzelfde id overschrijven elkaar.
    for (;;) {
      const r = await authFetch(`${BASIS}/uploads/${start.asset_id}/blokken/${nr}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/octet-stream' }, body: stuk, retry: true });
      if (r.ok) break;
      poging += 1;
      if (poging >= 3 || r.status < 500) await leesJson(r, 'Uploaden');
    }
    onVoortgang?.((nr + 1) / aantal);
  }
  return api(`/uploads/${start.asset_id}/klaar`, { method: 'POST', body: { blokken: aantal }, wat: 'Upload afronden' });
}

export const UPLOAD_TYPES = 'video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp,image/gif,application/pdf';

// ── meerwerk, opmerkingen, inbox, bewijs ─────────────────
export const haalInbox = (alleenMijn) => api(`/inbox${alleenMijn ? '?alleen_mijn=true' : ''}`, { wat: 'Inbox laden' });
export const stuurOfferte = (crId, bedragCents, toelichting) => api(`/verzoeken/${crId}/offerte`, {
  method: 'POST', body: { bedrag_cents: bedragCents, toelichting }, wat: 'Offerte sturen' });
export const wijsVerzoekAf = (crId, toelichting) => api(`/verzoeken/${crId}/afwijzen`, {
  method: 'POST', body: { toelichting }, wat: 'Verzoek afwijzen' });
export const heropenFase = (faseId) => api(`/fases/${faseId}/heropenen`, { method: 'POST', wat: 'Fase heropenen' });
export const plaatsOpmerking = (itemId, tekst, parentId) => api(`/items/${itemId}/opmerkingen`, {
  method: 'POST', body: { tekst, parent_id: parentId || null }, wat: 'Opmerking plaatsen' });
export const zetOpgelost = (opmerkingId, opgelost) => api(`/opmerkingen/${opmerkingId}`, {
  method: 'PATCH', body: { opgelost }, wat: 'Opmerking bijwerken' });
export async function downloadBewijs(besluitId) {
  const r = await authFetch(`${BASIS}/besluiten/${besluitId}/bewijs.pdf`);
  if (!r.ok) await leesJson(r, 'Akkoordbewijs');
  const url = URL.createObjectURL(await r.blob());
  const naam = /filename="([^"]+)"/.exec(r.headers.get('Content-Disposition') || '')?.[1] || 'akkoordbewijs.pdf';
  const a = document.createElement('a');
  a.href = url;
  a.download = naam;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

export function euro(cents) {
  if (cents === null || cents === undefined) return '—';
  return (cents / 100).toLocaleString('nl-NL', { style: 'currency', currency: 'EUR' });
}

export const VERZOEK_STATUS = {
  requested: { label: 'Offerte nodig', kleur: '#b91c1c', bg: '#fee2e2' },
  quoted: { label: 'Wacht op klant', kleur: '#b45309', bg: '#fef3c7' },
  accepted: { label: 'Geaccepteerd', kleur: '#15803d', bg: '#dcfce7' },
  applied: { label: 'Uitgevoerd', kleur: '#15803d', bg: '#dcfce7' },
  declined: { label: 'Afgewezen', kleur: '#5f6b7a', bg: '#f1f3f6' },
};

export const haalAudit = (id) => api(`/projecten/${id}/audit`, { wat: 'Audit laden' });

// Alleen http(s)-links klikbaar maken (de server weigert al andere protocollen).
export const veiligeLink = (url) => (/^https?:\/\//i.test(String(url || '')) ? url : null);

export const FASE_STATUS = {
  locked: { label: 'Volgt', kleur: '#5f6b7a', bg: '#f1f3f6' },
  in_production: { label: 'In productie', kleur: '#1e40af', bg: '#e0e7ff' },
  awaiting_approval: { label: 'Wacht op klant', kleur: '#b45309', bg: '#fef3c7' },
  approved: { label: 'Akkoord', kleur: '#15803d', bg: '#dcfce7' },
  skipped: { label: 'Overgeslagen', kleur: '#5f6b7a', bg: '#f1f3f6' },
  reopened: { label: 'Heropend', kleur: '#b45309', bg: '#fef3c7' },
};

export const ITEM_STATUS = {
  pending: { label: 'Open', kleur: '#5f6b7a', bg: '#f1f3f6' },
  approved: { label: 'Akkoord', kleur: '#15803d', bg: '#dcfce7' },
  changes_requested: { label: 'Wijziging gevraagd', kleur: '#b91c1c', bg: '#fee2e2' },
};

export const INKT = '#1f2937';
export const STIL = '#5f6b7a';
export const LIJN = '#eceef1';
export const ACCENT = '#526075';
export const KOLOM_BG = '#fafbfc';
export const WAARSCHUWING = '#b45309';

export function datumTijd(iso) {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  return d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
